import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Search, X, Download, RefreshCw, Upload } from "lucide-react";
import TableContainer from "Common/TableContainer";
import { toast, ToastContainer } from "react-toastify";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";
import { exportToExcel } from "Common/Components/Excel";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import PaginationTest from "Common/Components/Paginationtest";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import Swal from "sweetalert2"; // <-- اضافه شد
import { useAssistantFilter } from "Common/Assistant/lib/pageFilter";

// ========== Types ==========
interface BakeryQuota {
    id: number;
    distribution_period_code: string;
    distribution_stage_code: string;
    city: string;
    buyer_code: string;
    buyer_name: string;
    baking_type: string;
    quota: number;
    distribution_period: string;
}

interface ApiResponse {
    total: number;
    size: number;
    pages: number;
    items: BakeryQuota[];
}

// ========== کامپوننت اصلی ==========
const BakeryQuotas = () => {
    const navigate = useNavigate();
    const [quotas, setQuotas] = useState<BakeryQuota[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedNumber, setSelectedNumber] = useState<number>(10);
    const requestId = useRef(0);

    // به ازای هر حرف درخواست نزن؛ بعد از مکث کوتاه عبارت نهایی جست‌وجو شود.
    // مکث کمی بلندتر جلوی درخواست‌های اضافه را وقتی کاربر بین کلمه‌ها مکث
    // می‌کند می‌گیرد؛ درخواست قبلی هم با AbortController لغو می‌شود.
    useEffect(() => {
        const timer = setTimeout(
            () => setDebouncedSearch(searchTerm.trim()),
            searchTerm.trim() ? 350 : 0
        );
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // فیلتری که دستیار سامانه فرستاده (از گفتگو یا با صدا) را برمی‌دارد
    useAssistantFilter("quota", (filter) => {
        if (filter.pageSize) setSelectedNumber(filter.pageSize);
        const search = filter.search || "";
        setSearchTerm(search);
        setDebouncedSearch(search.trim());
        setCurrentPage(1);
    });

    // جلوگیری از overflow body
    useEffect(() => {
        document.body.classList.remove('overflow-hidden');
        document.body.style.overflow = '';

        const observer = new MutationObserver(() => {
            if (document.body.classList.contains('overflow-hidden')) {
                document.body.classList.remove('overflow-hidden');
                document.body.style.overflow = '';
            }
        });

        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);

    // ===== دریافت لیست صفحه‌بندی شده =====
    const fetchQuotas = useCallback(async (page: number, size: number, signal?: AbortSignal) => {
        const currentRequest = ++requestId.current;
        setLoading(true);
        setErrorMessage("");
        try {
            const params: any = { page, size };
            if (debouncedSearch) params.search = debouncedSearch;
            const res = await axios.get<ApiResponse>(
                "https://rasad-api.havirard.ir/monitoring/bakery-quotas/",
                {
                    params,
                    headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                    signal,
                }
            );
            if (currentRequest !== requestId.current) return;
            const items = Array.isArray(res.data.items) ? res.data.items : [];
            setQuotas(items.slice(0, size));
            setTotal(res.data.total ?? 0);
            setTotalPages(res.data.pages ?? 0);
        } catch (err: any) {
            if (signal?.aborted || currentRequest !== requestId.current) return;
            if (err.response?.status === 401) {
                localStorage.clear();
                navigate("/login");
            } else {
                setErrorMessage("خطا در دریافت لیست سهمیه‌ها");
                setQuotas([]);
            }
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, [debouncedSearch, navigate]);

    useEffect(() => {
        const controller = new AbortController();
        fetchQuotas(currentPage, selectedNumber, controller.signal);
        return () => controller.abort();
    }, [currentPage, selectedNumber, fetchQuotas]);

    // ===== دریافت همه‌ی داده‌ها برای اکسل =====
    const fetchAllQuotas = async (search?: string) => {
        let allItems: BakeryQuota[] = [];
        let page = 1;
        const size = 100;
        let hasMore = true;

        while (hasMore) {
            try {
                const params: any = { page, size };
                if (search?.trim()) params.search = search.trim();

                const res = await axios.get<ApiResponse>(
                    "https://rasad-api.havirard.ir/monitoring/bakery-quotas/",
                    {
                        params,
                        headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                    }
                );
                const items = res.data.items || [];
                allItems = [...allItems, ...items];
                if (items.length < size) {
                    hasMore = false;
                } else {
                    page++;
                }
            } catch (error) {
                console.error("خطا در دریافت صفحه", page, error);
                hasMore = false;
                throw error;
            }
        }
        return allItems;
    };

    // ===== خروجی اکسل با SweetAlert2 =====
    const handleExportExcel = async () => {
        const token = localStorage.getItem("app_havir");
        if (!token) {
            toast.error("لطفاً مجدداً وارد شوید");
            navigate("/login");
            return;
        }

        // نمایش مودال لودینگ
        Swal.fire({
            title: "در حال دریافت اطلاعات",
            text: "لطفاً منتظر بمانید...",
            icon: "info",
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        try {
            const allQuotas = await fetchAllQuotas(searchTerm);

            if (allQuotas.length === 0) {
                Swal.close();
                toast.warning("هیچ سهمیه‌ای برای خروجی وجود ندارد");
                return;
            }

            // ساخت دیتای اکسل
            const exportData = allQuotas.map((q) => ({
                "شناسه": q.id,
                "دوره": q.distribution_period,
                "کد دوره": q.distribution_period_code,
                "مرحله توزیع": q.distribution_stage_code,
                "شهرستان": q.city,
                "کد نانوا": q.buyer_code || "-",
                "نام نانوا": q.buyer_name || "-",
                "نوع پخت": q.baking_type || "-",
                "سهمیه": q.quota != null ? Number(q.quota).toFixed(1) : "-",
            }));

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "سهمیه خبازی‌ها");
            XLSX.writeFile(wb, `سهمیه_خبازی_ها_${new Date().toLocaleDateString('fa-IR')}.xlsx`);

            Swal.close(); // بستن مودال لودینگ
            toast.success(`${allQuotas.length} رکورد با موفقیت صادر شد`);
        } catch (err: any) {
            Swal.close(); // بستن مودال لودینگ
            console.error("Export error:", err);
            if (err.response?.status === 401) {
                toast.error("نشست شما منقضی شده است، لطفاً مجدداً وارد شوید");
                navigate("/login");
            } else {
                toast.error("خطا در دریافت یا خروجی اطلاعات");
            }
        }
    };

    // ===== هندلرها =====
    const handlePageChange = (page: number) => setCurrentPage(page);
    const handleSizeChange = (value: number) => {
        setSelectedNumber(value);
        setCurrentPage(1);
    };
    const handleSearchChange = (val: string) => {
        setSearchTerm(val);
        setCurrentPage(1);
    };

    // ===== ستون‌های جدول =====
    const columns = useMemo(
        () => [
            {
                header: "نام نانوا",
                accessorKey: "buyer_name",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "کد نانوا",
                accessorKey: "buyer_code",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "دوره",
                accessorKey: "distribution_period",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "سهمیه",
                accessorKey: "quota",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const val = cell.getValue();
                    return val != null ? Number(val).toFixed(1) : "-";
                },
            },
            {
                header: "شهرستان",
                accessorKey: "city",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "نوع پخت",
                accessorKey: "baking_type",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
        ],
        []
    );

    // ===== رندر =====
    return (
        <React.Fragment>
            <BreadCrumb pageTitle="سهمیه خبازی‌ها" title="پایش آرد" />
            <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />

            <div className="card">
                <div className="card-body">
                    <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-2">
                            <RefreshCw
                                className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`}
                                onClick={() => fetchQuotas(currentPage, selectedNumber)}
                            />
                            <h6 className="text-lg font-semibold">
                                مجموع سهمیه‌ها{" "}
                                <span className="px-2 py-0.5 text-sm bg-blue-100 text-blue-700 rounded-full">
                                    {total}
                                </span>
                            </h6>
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="جستجو..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    className="w-64 pr-10 pl-4 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                                />
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
                                {searchTerm && (
                                    <button onClick={() => handleSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <X className="size-4" />
                                    </button>
                                )}
                            </div>
                            <TableDropDownSmall onSelect={handleSizeChange} />
                            <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50">
                                <Download size={16} /> اکسل
                            </button>
                            {/* فقط برای ادمین دکمهٔ آپلود */}
                            <button onClick={() => navigate("/BakeryQuotas/upload")} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-xl hover:bg-green-700">
                                <Upload size={16} /> بارگذاری اکسل
                            </button>
                        </div>
                    </div>

                    {loading && quotas.length > 0 && (
                        <div className="mb-2 text-xs text-slate-500 dark:text-zink-200">
                            در حال به‌روزرسانی نتایج…
                        </div>
                    )}

                    {loading && quotas.length === 0 ? (
                        <LoadingCounter count={0} text="در حال بارگذاری..." />
                    ) : quotas.length === 0 ? (
                        <LoadingCounter count={0} text={errorMessage || "سهمیه‌ای یافت نشد"} />
                    ) : (
                        <>
                            <TableContainer
                                isPagination={false}
                                columns={columns}
                                data={quotas}
                                customPageSize={selectedNumber}
                                divclassName="-mx-5 overflow-x-auto"
                                tableclassName="w-full whitespace-nowrap"
                                theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
                                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                                PaginationClassName="hidden"
                            />
                            <PaginationTest
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};

export default BakeryQuotas;
// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import BreadCrumb from "Common/BreadCrumb";
// import { Search, X, Download, RefreshCw, Upload } from "lucide-react";
// import TableContainer from "Common/TableContainer";
// import { toast, ToastContainer } from "react-toastify";
// import TableDropDownSmall from "pages/Components/TableDropDownSmall";
// import { exportToExcel } from "Common/Components/Excel";
// import LoadingCounter from "Common/Components/LoadingCounterProps";
// import PaginationTest from "Common/Components/Paginationtest";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import * as XLSX from 'xlsx'; // برای خروجی اکسل

// // ========== Types ==========
// interface BakeryQuota {
//     id: number;
//     distribution_period_code: string;
//     distribution_stage_code: string;
//     city: string;
//     buyer_code: string;        // کد نانوا
//     buyer_name: string;
//     baking_type: string;        // انواع پخت
//     quota: number;              // سهمیه (با یک رقم اعشار)
//     distribution_period: string; // دورهٔ خوانا (مثال: دوره خبازی خرداد ۱۴۰۵)
// }

// interface ApiResponse {
//     total: number;
//     size: number;
//     pages: number;
//     items: BakeryQuota[];
// }

// // ========== کامپوننت اصلی ==========
// const BakeryQuotas = () => {
//     const navigate = useNavigate();
//     const [quotas, setQuotas] = useState<BakeryQuota[]>([]);
//     const [loading, setLoading] = useState(false);
//     const [errorMessage, setErrorMessage] = useState("");
//     const [total, setTotal] = useState(0);
//     const [totalPages, setTotalPages] = useState(0);

//     const [searchTerm, setSearchTerm] = useState("");
//     const [currentPage, setCurrentPage] = useState(1);
//     const [selectedNumber, setSelectedNumber] = useState<number>(10);

//     // جلوگیری از overflow body
//     useEffect(() => {
//         document.body.classList.remove('overflow-hidden');
//         document.body.style.overflow = '';

//         const observer = new MutationObserver(() => {
//             if (document.body.classList.contains('overflow-hidden')) {
//                 document.body.classList.remove('overflow-hidden');
//                 document.body.style.overflow = '';
//             }
//         });

//         observer.observe(document.body, {
//             attributes: true,
//             attributeFilter: ['class'],
//         });

//         return () => observer.disconnect();
//     }, []);

//     // ===== دریافت لیست صفحه‌بندی شده =====
//     const fetchQuotas = useCallback(async (page: number, size: number) => {
//         setLoading(true);
//         setErrorMessage("");
//         try {
//             const params: any = { page, size };
//             if (searchTerm.trim()) params.search = searchTerm.trim();
//             const res = await axios.get<ApiResponse>(
//                 "https://rasad-api.havirard.ir/monitoring/bakery-quotas/",
//                 {
//                     params,
//                     headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
//                 }
//             );
//             setQuotas(res.data.items ?? []);
//             setTotal(res.data.total ?? 0);
//             setTotalPages(res.data.pages ?? 0);
//         } catch (err: any) {
//             if (err.response?.status === 401) {
//                 localStorage.clear();
//                 navigate("/login");
//             } else {
//                 setErrorMessage("خطا در دریافت لیست سهمیه‌ها");
//                 setQuotas([]);
//             }
//         } finally {
//             setLoading(false);
//         }
//     }, [searchTerm, navigate]);

//     useEffect(() => {
//         fetchQuotas(currentPage, selectedNumber);
//     }, [currentPage, selectedNumber, fetchQuotas]);

//     // ===== دریافت همه‌ی داده‌ها برای اکسل =====
//     const fetchAllQuotas = async (search?: string) => {
//         let allItems: BakeryQuota[] = [];
//         let page = 1;
//         const size = 100; // حداکثر مجاز
//         let hasMore = true;

//         while (hasMore) {
//             try {
//                 const params: any = { page, size };
//                 if (search?.trim()) params.search = search.trim();

//                 const res = await axios.get<ApiResponse>(
//                     "https://rasad-api.havirard.ir/monitoring/bakery-quotas/",
//                     {
//                         params,
//                         headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
//                     }
//                 );
//                 const items = res.data.items || [];
//                 allItems = [...allItems, ...items];
//                 if (items.length < size) {
//                     hasMore = false;
//                 } else {
//                     page++;
//                 }
//             } catch (error) {
//                 console.error("خطا در دریافت صفحه", page, error);
//                 hasMore = false;
//                 throw error;
//             }
//         }
//         return allItems;
//     };

//     // ===== خروجی اکسل =====
//     const handleExportExcel = async () => {
//         const token = localStorage.getItem("app_havir");
//         if (!token) {
//             toast.error("لطفاً مجدداً وارد شوید");
//             navigate("/login");
//             return;
//         }

//         toast.info("در حال دریافت اطلاعات، لطفاً صبر کنید...");

//         try {
//             // دریافت همه سهمیه‌ها با فیلتر جستجوی فعلی
//             const allQuotas = await fetchAllQuotas(searchTerm);

//             if (allQuotas.length === 0) {
//                 toast.warning("هیچ سهمیه‌ای برای خروجی وجود ندارد");
//                 return;
//             }

//             // ساخت دیتای اکسل
//             const exportData = allQuotas.map((q) => ({
//                 "شناسه": q.id,
//                 "دوره": q.distribution_period,
//                 "کد دوره": q.distribution_period_code,
//                 "مرحله توزیع": q.distribution_stage_code,
//                 "شهرستان": q.city,
//                 "کد نانوا": q.buyer_code || "-",
//                 "نام نانوا": q.buyer_name || "-",
//                 "نوع پخت": q.baking_type || "-",
//                 "سهمیه": q.quota != null ? Number(q.quota).toFixed(1) : "-",
//             }));

//             const ws = XLSX.utils.json_to_sheet(exportData);
//             const wb = XLSX.utils.book_new();
//             XLSX.utils.book_append_sheet(wb, ws, "سهمیه خبازی‌ها");
//             XLSX.writeFile(wb, `سهمیه_خبازی_ها_${new Date().toLocaleDateString('fa-IR')}.xlsx`);

//             toast.success(`${allQuotas.length} رکورد با موفقیت صادر شد`);
//         } catch (err: any) {
//             console.error("Export error:", err);
//             if (err.response?.status === 401) {
//                 toast.error("نشست شما منقضی شده است، لطفاً مجدداً وارد شوید");
//                 navigate("/login");
//             } else {
//                 toast.error("خطا در دریافت یا خروجی اطلاعات");
//             }
//         }
//     };

//     // ===== هندلرها =====
//     const handlePageChange = (page: number) => setCurrentPage(page);
//     const handleSizeChange = (value: number) => {
//         setSelectedNumber(value);
//         setCurrentPage(1);
//     };
//     const handleSearchChange = (val: string) => {
//         setSearchTerm(val);
//         setCurrentPage(1);
//     };

//     // ===== ستون‌های جدول =====
//     const columns = useMemo(
//         () => [
//             {
//                 header: "نام نانوا",
//                 accessorKey: "buyer_name",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() || "-",
//             },
//             {
//                 header: "کد نانوا",
//                 accessorKey: "buyer_code",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() || "-",
//             },
//             {
//                 header: "دوره",
//                 accessorKey: "distribution_period",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() || "-",
//             },
//             {
//                 header: "سهمیه",
//                 accessorKey: "quota",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const val = cell.getValue();
//                     return val != null ? Number(val).toFixed(1) : "-";
//                 },
//             },
//             {
//                 header: "شهرستان",
//                 accessorKey: "city",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() || "-",
//             },
//             {
//                 header: "نوع پخت",
//                 accessorKey: "baking_type",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() || "-",
//             },
//         ],
//         []
//     );

//     // ===== رندر =====
//     return (
//         <React.Fragment>
//             <BreadCrumb pageTitle="سهمیه خبازی‌ها" title="پایش آرد" />
//             <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />

//             <div className="card">
//                 <div className="card-body">
//                     <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
//                         <div className="flex items-center gap-2">
//                             <RefreshCw
//                                 className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`}
//                                 onClick={() => fetchQuotas(currentPage, selectedNumber)}
//                             />
//                             <h6 className="text-lg font-semibold">
//                                 مجموع سهمیه‌ها{" "}
//                                 <span className="px-2 py-0.5 text-sm bg-blue-100 text-blue-700 rounded-full">
//                                     {total}
//                                 </span>
//                             </h6>
//                         </div>
//                         <div className="flex flex-wrap gap-3 items-center">
//                             <div className="relative">
//                                 <input
//                                     type="text"
//                                     placeholder="جستجو..."
//                                     value={searchTerm}
//                                     onChange={(e) => handleSearchChange(e.target.value)}
//                                     className="w-64 pr-10 pl-4 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
//                                 />
//                                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
//                                 {searchTerm && (
//                                     <button onClick={() => handleSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2">
//                                         <X className="size-4" />
//                                     </button>
//                                 )}
//                             </div>
//                             <TableDropDownSmall onSelect={handleSizeChange} />
//                             <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50">
//                                 <Download size={16} /> اکسل
//                             </button>
//                             {/* فقط برای ادمین دکمهٔ آپلود */}
//                             <button onClick={() => navigate("/BakeryQuotas/upload")} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-xl hover:bg-green-700">
//                                 <Upload size={16} /> بارگذاری اکسل
//                             </button>
//                         </div>
//                     </div>

//                     {loading ? (
//                         <LoadingCounter count={0} text="در حال بارگذاری..." />
//                     ) : quotas.length === 0 ? (
//                         <LoadingCounter count={0} text={errorMessage || "سهمیه‌ای یافت نشد"} />
//                     ) : (
//                         <>
//                             <TableContainer
//                                 isPagination={false}
//                                 columns={columns}
//                                 data={quotas}
//                                 customPageSize={selectedNumber}
//                                 divclassName="-mx-5 overflow-x-auto"
//                                 tableclassName="w-full whitespace-nowrap"
//                                 theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
//                                 thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
//                                 tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
//                                 PaginationClassName="hidden"
//                             />
//                             <PaginationTest
//                                 currentPage={currentPage}
//                                 totalPages={totalPages}
//                                 onPageChange={handlePageChange}
//                             />
//                         </>
//                     )}
//                 </div>
//             </div>
//         </React.Fragment>
//     );
// };

// export default BakeryQuotas;
